import { useEffect } from 'react';
import {
  Anchor,
  Button,
  Checkbox,
  FileInput,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconPaperclip } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, rawRequest, unwrap } from '../../api/client';
import { fromApiDate, toApiDate } from '../../lib/format';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';
import { saveDocument, type DocumentRecord } from './documentApi';
import { DOCUMENT_KINDS, type DocumentKind } from './documentKinds';

type FormValues = {
  type_id: string;
  reference: string;
  issuer: string;
  issued_on: Date | null;
  valid_from: Date | null;
  valid_until: Date | null;
  warning_days: number | '';
  is_suspended: boolean;
  is_active: boolean;
  language: string;
  level: number | '';
  limitations: string;
  notes: string;
  scan: File | null;
};

/**
 * Adding or editing one piece of pilot paperwork.
 *
 * Expiry is left empty on purpose where the backend can derive it - a medical from the holder's
 * age at examination, a language assessment from its ICAO level - so the rule lives in one
 * place rather than being retyped by whoever files the scan.
 */
export function DocumentModal({
  opened,
  onClose,
  kind,
  personId,
  record,
}: {
  opened: boolean;
  onClose: () => void;
  kind: DocumentKind;
  personId: number;
  record?: DocumentRecord | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const spec = DOCUMENT_KINDS[kind];

  const types = useQuery({
    queryKey: ['taxonomy', spec.taxonomyPath],
    queryFn: async () => fetchTaxonomy(spec.taxonomyPath!),
    enabled: opened && Boolean(spec.taxonomyPath),
    staleTime: 10 * 60 * 1000,
  });

  const form = useForm<FormValues>({
    initialValues: {
      type_id: '',
      reference: '',
      issuer: '',
      issued_on: null,
      valid_from: null,
      valid_until: null,
      warning_days: 60,
      is_suspended: false,
      is_active: true,
      language: 'English',
      level: 4,
      limitations: '',
      notes: '',
      scan: null,
    },
    validate: {
      type_id: (value) => (!spec.taxonomyPath || value ? null : t('common.requiredField')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      type_id: record?.type_id ? String(record.type_id) : '',
      reference: record?.reference ?? '',
      issuer: record?.issuer ?? '',
      issued_on: fromApiDate(record?.issued_on),
      valid_from: fromApiDate(record?.valid_from),
      valid_until: fromApiDate(record?.valid_until),
      warning_days: record?.warning_days ?? 60,
      is_suspended: record?.is_suspended ?? false,
      is_active: record?.is_active ?? true,
      language: record?.language ?? 'English',
      level: record?.level ?? 4,
      limitations: record?.limitations ?? '',
      notes: record?.notes ?? '',
      scan: null,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, record?.id, kind]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body: Record<string, unknown> = {
        person: personId,
        valid_from: toApiDate(values.valid_from),
        valid_until: toApiDate(values.valid_until),
        warning_days: values.warning_days === '' ? 60 : values.warning_days,
        is_suspended: values.is_suspended,
        is_active: values.is_active,
        notes: values.notes,
      };
      if (spec.typeField) body[spec.typeField] = Number(values.type_id);
      if (spec.referenceField) body[spec.referenceField.name] = values.reference.trim();
      if (spec.issuerField) body[spec.issuerField.name] = values.issuer.trim();
      if (spec.issuedField) body[spec.issuedField.name] = toApiDate(values.issued_on);
      if (kind === 'language') {
        body.language = values.language.trim();
        body.level = values.level === '' ? 4 : values.level;
      }
      if (kind === 'medical') body.limitations = values.limitations.trim();

      const savedId = await saveDocument(kind, record?.id ?? null, body);

      // The scan is multipart, so it goes up separately once the record has an id.
      if (values.scan) await uploadScan(spec.path, savedId, values.scan);
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      await queryClient.invalidateQueries({ queryKey: ['person'] });
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const typeOptions = (types.data ?? []).map((row) => ({
    value: String(row.id),
    label: row.code ? `${row.code} — ${row.name}` : row.name,
  }));

  return (
    <Modal opened={opened} onClose={onClose} title={t(spec.titleKey)} size="lg">
      <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
        <Stack gap="md">
          {spec.taxonomyPath ? (
            <Select
              label={t(spec.titleKey)}
              required
              data={typeOptions}
              searchable
              allowDeselect={false}
              disabled={Boolean(record)}
              {...form.getInputProps('type_id')}
            />
          ) : null}

          {kind === 'language' ? (
            <Group grow align="flex-start">
              <TextInput label={t('common.language')} {...form.getInputProps('language')} />
              <NumberInput
                label={t('staff.icaoLevel')}
                min={1}
                max={6}
                {...form.getInputProps('level')}
              />
            </Group>
          ) : null}

          <Group grow align="flex-start">
            {spec.referenceField ? (
              <TextInput
                label={t(spec.referenceField.labelKey)}
                {...form.getInputProps('reference')}
              />
            ) : null}
            {spec.issuerField ? (
              <TextInput label={t(spec.issuerField.labelKey)} {...form.getInputProps('issuer')} />
            ) : null}
          </Group>

          <Group grow align="flex-start">
            {spec.issuedField ? (
              <DatePickerInput
                label={t(spec.issuedField.labelKey)}
                valueFormat="DD.MM.YYYY"
                clearable
                {...form.getInputProps('issued_on')}
              />
            ) : null}
            <DatePickerInput
              label={t('common.validUntil')}
              description={t('staff.derivedExpiryHint')}
              valueFormat="DD.MM.YYYY"
              clearable
              {...form.getInputProps('valid_until')}
            />
          </Group>

          {kind === 'medical' ? (
            <TextInput label={t('staff.limitations')} {...form.getInputProps('limitations')} />
          ) : null}

          <Group grow align="flex-start">
            <NumberInput
              label={t('staff.warningDays')}
              min={0}
              max={365}
              {...form.getInputProps('warning_days')}
            />
            <FileInput
              label={t('staff.scan')}
              leftSection={<IconPaperclip size={15} />}
              accept="application/pdf,image/*"
              clearable
              {...form.getInputProps('scan')}
            />
          </Group>

          {record?.scan ? (
            <Anchor href={record.scan} target="_blank" rel="noreferrer" fz="sm">
              {t('staff.scan')}
            </Anchor>
          ) : null}

          <Group gap="lg">
            <Checkbox
              label={t('common.active')}
              {...form.getInputProps('is_active', { type: 'checkbox' })}
            />
            <Checkbox
              label={t('staff.suspended')}
              {...form.getInputProps('is_suspended', { type: 'checkbox' })}
            />
          </Group>

          <Textarea
            label={t('common.notes')}
            autosize
            minRows={2}
            {...form.getInputProps('notes')}
          />

          {record?.allows_ctr_entry === false && kind === 'language' ? (
            <Text fz="xs" c="orange">
              {t('staff.ctrEntry')}
            </Text>
          ) : null}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

type TaxonomyRow = { id: number; code?: string | null; name: string };

async function fetchTaxonomy(path: NonNullable<typeof DOCUMENT_KINDS.licence.taxonomyPath>) {
  const query = { page_size: 200, is_active: true } as const;
  switch (path) {
    case 'taxonomy/licence-types': {
      const data = unwrap(
        await api.GET('/api/v1/taxonomy/licence-types/', { params: { query } }),
      );
      return data.results as TaxonomyRow[];
    }
    case 'taxonomy/rating-types': {
      const data = unwrap(await api.GET('/api/v1/taxonomy/rating-types/', { params: { query } }));
      return data.results as TaxonomyRow[];
    }
    case 'taxonomy/certificate-types': {
      const data = unwrap(
        await api.GET('/api/v1/taxonomy/certificate-types/', { params: { query } }),
      );
      return data.results as TaxonomyRow[];
    }
    case 'taxonomy/medical-classes': {
      const data = unwrap(
        await api.GET('/api/v1/taxonomy/medical-classes/', { params: { query } }),
      );
      return data.results as TaxonomyRow[];
    }
  }
}

async function uploadScan(path: string, id: number, file: File) {
  const body = new FormData();
  body.append('scan', file);
  await rawRequest(`/${path}/${id}/`, { method: 'PATCH', body });
}
